import { Group, Text, Tooltip } from '@mantine/core';
import { BookOpen, Clipboard, Clock, TrendingUp } from 'react-feather';
import { Sparkles } from 'lucide-react';
import type { ComponentType } from 'react';
import type { PreSessionGuidanceSourceType } from '../../../../services/api/aiTypes';

/**
 * MED-876 AC5 — source chip variants for the Session Leader Guidance items
 * in the Sentiment & Guidance panel. The `type` drives the chip variant; the
 * `detail` is the short label rendered inside the chip (and the tooltip).
 *
 * Visual reference: screenshot #3 (panel close-up).
 */
export interface SourceChipProps {
  type: PreSessionGuidanceSourceType;
  detail: string;
}

// MED-1100 — per-source-type tag treatment, tokens from the Figma frame
// (node 1993:18080). Tags render in their data casing (no uppercase), 10px bold,
// 4px radius, color-coded per source.
type IconType = ComponentType<{ size?: number | string; color?: string }>;
const VARIANTS: Record<
  PreSessionGuidanceSourceType,
  { label: (detail: string) => string; bg: string; fg: string; Icon: IconType }
> = {
  reflection: {
    label: detail => detail,
    bg: '#f3f0ff',
    fg: '#6b4ca0',
    Icon: Sparkles,
  },
  past_session: {
    label: detail => detail,
    bg: '#dbeafe',
    fg: '#1d4ed8',
    Icon: Clock,
  },
  attendance_pattern: {
    label: detail => detail,
    bg: '#fef3e2',
    fg: '#92400e',
    Icon: TrendingUp,
  },
  medley_guidance: {
    label: detail => detail,
    bg: '#e6f4f3',
    fg: '#2d7b77',
    Icon: BookOpen,
  },
  // MED-1208 (D14) — intake/matching-form-grounded guidance.
  matching_form: {
    label: detail => detail,
    bg: '#ecfdf5',
    fg: '#047857',
    Icon: Clipboard,
  },
};

export function SourceChip({ type, detail }: SourceChipProps) {
  // Skew guard: a source type this bundle doesn't know yet renders as the
  // neutral methodology chip instead of crashing on undefined.
  const variant = VARIANTS[type] ?? VARIANTS.medley_guidance;
  const Icon = variant.Icon;
  return (
    <Tooltip label={detail} position="bottom" withArrow>
      <Group
        gap={3}
        align="center"
        wrap="nowrap"
        style={{
          backgroundColor: variant.bg,
          color: variant.fg,
          padding: '2px 8px',
          borderRadius: 4,
          display: 'inline-flex',
          maxWidth: '100%',
        }}
      >
        <Icon size={13} color={variant.fg} />
        <Text
          fz={12}
          fw={700}
          style={{ color: variant.fg, lineHeight: 1.5, whiteSpace: 'nowrap' }}
        >
          {variant.label(detail)}
        </Text>
      </Group>
    </Tooltip>
  );
}

export default SourceChip;
